<template>
  <div class="delivery-address-form">
    <div class="delivery-address-form__form-group">
      <delivery-address-name
        v-if="LOGGED_IN"
        v-model="addressName"
        ref="addressName"
        :addresses="addresses"
        :selected-address-id="selectedAddressId"
        :is-save-address="isSaveAddress"
        :is-show-save-address="isShowSaveAddress"
        @new="resetAddress"
        @set="setAddress"
        class="delivery-address-form__address"
      />

      <ui-street-suggest
        v-model="street"
        ref="street"
        :city-id="cityId"
        class="delivery-address-form__street"
      />
    </div>

    <div class="delivery-address-form__form-group delivery-address-form__form-group_grid">
      <ui-house-suggest
        v-model="house"
        ref="house"
        :street-id="form.streetId"
        label="Дом *"
        placeholder="Дом, стр./кор."
        class="delivery-address-form__house"
      />

      <ui-input
        label="Подъезд"
        placeholder="Подъезд"
        v-model="form.entrance"
        validate="address_detail"
        ref="entrance"
        clearable
        class="delivery-address-form__entrance"
      />

      <ui-input
        label="Этаж"
        placeholder="Этаж"
        v-model="form.floor"
        validate="floor"
        ref="floor"
        clearable
        class="delivery-address-form__floor"
      />

      <ui-input
        label="Кв./Офис"
        placeholder="Кв./Офис"
        v-model="form.apartment"
        validate="address_detail"
        ref="apartment"
        clearable
        class="delivery-address-form__apartment"
      />
    </div>

    <delivery-date
      v-model="form.intervalTimeId"
      class="delivery-address-form__date"
    />

    <ui-textarea
      v-model="comment"
      height="128"
      label="Комментарий"
      placeholder="Комментарий к заказу"
      class="delivery-address-form__comment"
    />

    <div class="delivery-address-form__delivery-price">
      <b>Стоимость доставки:</b> {{ deliveryPrice | price }} ₽
    </div>

    <div class="delivery-address-form__footer">
      <ui-checkbox
        v-if="isShowSaveAddress"
        v-model="isSaveAddress"
        label="Сохранить адрес в Адреса доставки"
        class="delivery-address-form__save-address"
      />

      <ui-button
        text="Сохранить"
        class="delivery-address-form__save-btn"
        @click="onSave"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

import UiHouseSuggest from '@kit/ui-house-suggest/ui-house-suggest.vue';
import DeliveryDate from './delivery-date/delivery-date.vue';
import DeliveryAddressName from './delivery-address-name.vue';

// ключи для сравнения с выбранным адреcом
const compareKeys = [
  'cityId',
  'streetId',
  'streetName',
  'building',
  'entrance',
  'floor',
  'house',
  'houseBlock',
  'postalCode',
  'apartment',
  'intervalTimeId',
];

export const defaultAddress = {
  cityId: '',
  cityName: '',
  metroStationId: '',
  streetId: '',
  streetName: '',
  house: '',
  houseBlock: '',
  building: '',
  apartment: '',
  entrance: '',
  floor: '',
  postalCode: '',
  intervalTimeId: '',
};

export default {
  name: 'delivery-address-form',

  components: {
    DeliveryAddressName,
    UiHouseSuggest,
    DeliveryDate,
  },

  props: {
    currentCity: {
      type: Object,
      default: () => ({}),
    },
    deliveryPrice: {
      type: [Number, String],
      default: 0,
    },
  },

  data: () => ({
    comment: '',
    addressName: '',
    isFormLoaded: false,
    isSaveAddress: false,
    form: { ...defaultAddress },
  }),

  computed: {
    ...mapState({
      CART_COMMENT: state => state.cart.comment,
    }),
    ...mapGetters({
      ADDRESSES: 'user/addresses',
      LOGGED_IN: 'user/loggedIn',
      DELIVERY_INFO: 'cart/deliveryInfo',
      SELECTED_DATE: 'cart/deliverySelectedDate',
    }),

    house: {
      get() {
        return {
          number: this.form.house,
          block: this.form.houseBlock,
          building: this.form.building,
        };
      },
      set(value) {
        this.form.house = value?.number;
        this.form.houseBlock = value?.block;
        this.form.building = value?.building;
        this.form.postalCode = value?.postalCode;
      },
    },

    street: {
      get() {
        return { id: this.form.streetId, name: this.form.streetName };
      },
      set({ id = '', name = '' } = {}) {
        this.form.streetId = id;
        this.form.streetName = name;
      },
    },

    cityId() {
      return this.form.cityId || this.currentCity?.id;
    },

    addresses() {
      return this.ADDRESSES.filter(a => a.cityId === this.cityId);
    },

    selectedAddressId() {
      return this.addresses.find(this.addressCompare)?.id || null;
    },

    formWatchTrigger() {
      return JSON.stringify(this.form);
    },

    isShowSaveAddress() {
      return this.LOGGED_IN && !this.selectedAddressId && this.ADDRESSES.length < 10;
    },

    defaultAddress() {
      return {
        ...defaultAddress,
        cityId: this.currentCity?.id,
        cityName: this.currentCity?.name,
      };
    },
  },

  watch: {
    currentCity: {
      handler() {
        this.resetAddress();
      },
    },

    isShowSaveAddress: {
      immediate: true,
      handler(value) {
        this.isSaveAddress = value;
      },
    },

    formWatchTrigger() {
      this.$emit('change', { ...this.form });
    },

    'DELIVERY_INFO.address'(address = {}) {
      this.updateAddress(address, this.form);
    },

    'SELECTED_DATE.intervalId': {
      immediate: true,
      handler(intervalTimeId) {
        this.form.intervalTimeId = intervalTimeId;
      },
    },
  },

  async mounted() {
    this.updateAddress(this.DELIVERY_INFO?.address);
    this.comment = this.CART_COMMENT;

    if (this.LOGGED_IN) await this.LOAD_ADDRESSES();

    if (!this.form.streetId) {
      const primaryAddress = this.addresses.find(({ primary }) => primary);
      if (primaryAddress) this.updateAddress(primaryAddress);
    }
  },

  methods: {
    ...mapActions({
      UPDATE_CITY: 'user/updateCity',
      LOAD_ADDRESSES: 'user/loadAddresses',
    }),

    addressCompare(a) {
      // возвращает true если введенный адрес соответствует входящему
      return !compareKeys.some(k => !!a[k] && a[k] !== this.form[k]);
    },

    setAddress(id) {
      const address = this.addresses.find(a => a.id === id);
      if (address) this.updateAddress(address);
      this.comment = address?.comment || '';
      this.$nextTick(() => this.checkFormValidity()); // для сброса валидации
    },

    async checkFormValidity() {
      const { house, apartment, street, addressName, entrance, floor } = this.$refs;

      const isValid = [
        addressName ? addressName.checkValidOutside() : true,
        house.checkValidOutside(),
        apartment.checkValidOutside(),
        entrance.checkValidOutside(),
        floor.checkValidOutside(),
        street.checkValidOutside(),
      ].every(isValid => isValid === true);

      return Promise.resolve(isValid);
    },

    resetAddress() {
      this.form = {
        ...defaultAddress,
        cityId: this.currentCity?.id || '',
        cityName: this.currentCity?.name || '',
      };
      this.addressName = '';
      this.comment = '';
    },

    updateAddress(data = {}, defaultData = this.defaultAddress) {
      Object.keys(defaultAddress).forEach(key => this.form[key] = data[key] || defaultData[key]);

      if (!this.comment) this.comment = data.comment || '';
    },

    async onSave() {
      if (!await this.checkFormValidity()) return;

      if (this.isSaveAddress && this.addressName) { // сохранение адреса в аккаунт
        this.$store.$api.createAddress({
          address: {
            ...this.form,
            comment: this.comment,
            name: this.addressName,
          },
        });
      }

      this.$emit('save', { ...this.form, comment: this.comment });
    },
  },
};
</script>

<style lang="scss">
.delivery-address-form {
  &__form-group {
    display: flex;
    flex-direction: column;

    &_grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      column-gap: 16px;
      row-gap: 0;
    }

    .ui-input {
      p.error {
        margin-top: 0;
      }
    }
  }

  &__address {
    margin-bottom: 12px;
  }

  &__street,
  &__house {
    .ui-house-suggest__suggestions,
    .ui-street-suggest__suggestions {
      .ui-dropdown-scrollview > div {
        font-size: 14px;
      }
    }
  }

  &__street {
    width: 100%;
  }

  &__comment {
    margin-top: 12px;

    label {
      margin-bottom: 4px;
    }
  }

  &__delivery-price {
    margin-top: 16px;
    font-size: 14px;

    b {
      font-weight: 600;
    }
  }

  &__footer {
    margin-top: 16px;
  }

  &__save-btn {
    width: 100%;
    margin-top: 24px;
    line-height: 16px;
  }
}

@include breakpoint("tablet") {
  .delivery-address-form {
    &__form-group {
      flex-direction: row;

      &_grid {
        grid-template-columns: repeat(4, 1fr);
        column-gap: 20px;
      }
    }

    &__address {
      flex-shrink: 0;
      width: 174px;
      margin: 0 20px 0 0;
    }

    &__comment {
      margin-top: 16px;

      textarea {
        padding-top: 3px;
      }

      .ui-textarea-container {
        height: 80px !important;
      }
    }

    &__delivery-price {
      margin-top: 12px;
    }

    &__footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 20px;
    }

    &__save-btn {
      width: 176px;
      margin: 0 0 0 auto;
      line-height: 24px;
    }
  }
}

@include breakpoint("desktop") {
  .delivery-address-form {
    &__form-group {
      &_grid {
        column-gap: 16px;
      }
    }

    &__address {
      flex-shrink: 0;
      margin-right: 16px;
    }

    &__comment {
      .ui-textarea-container {
        height: 80px !important;
      }
    }

    &__delivery-price {
      margin-top: 16px;
    }

    &__footer {
      margin-top: 14px;
    }
  }
}
</style>
